import {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
  memo,
} from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  CircularProgress,
  Autocomplete,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useDispatch, useSelector } from "react-redux";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/EditOutlined";
import {
  getEventsTypeData,
  saveOrderByDocumentation,
  getOrderByDocumentation,
  deleteOrderByDocumentation,
} from "../../../../store/actions/maintenanceOrderByActions";
import { getDocumentTypeData } from "../../../../store/actions/documentAction";
import { GET } from "../../../../utils/axios";
import EmailInputField from "../../../../components/EmailInputField";
import { normalizeEmails, isValidEmailList } from "../../../../utils/emailValidation";

// ---------------------------------------------------------------------------
// Helpers — module-level, never recreated
// ---------------------------------------------------------------------------
const SHIPPER_SEARCH_MIN_CHARS = 2;
const MAX_SHIPPER_OPTIONS = 100;

// ---------------------------------------------------------------------------
// FormPanel
//
// Owns ALL form state including shipper search/options/loading.
// Parent never re-renders from anything happening inside this component.
// memo + all callback props are stable ([] deps in parent via ref-forwarding).
// ---------------------------------------------------------------------------
const FormPanel = memo(function FormPanel({
  documentTypeOptions,   // stable after master data loads (same array ref)
  eventsTypeOptions,     // stable after master data loads
  locationNumberRef,     // ref → always current locationNumber, zero re-renders
  editingRow,
  resetKey,              // increments on every new-add to force form reset
  saving,
  onSubmit,              // stable [] dep callback
  onCancelEdit,          // stable [] dep callback
}) {
  const [documentTypes, setDocumentTypes] = useState([]);
  const [eventTypes,    setEventTypes]    = useState([]);
  const [shipper,       setShipper]       = useState(null);
  const [email,         setEmail]         = useState([]);
  const [formError,     setFormError]     = useState("");

  // Shipper search lives entirely here — no parent involvement at all
  const [shipperOptions, setShipperOptions] = useState([]);
  const [shipperLoading, setShipperLoading] = useState(false);
  const [shipperInputValue, setShipperInputValue] = useState("");
  const shipperDebounceRef = useRef(null);

  const handleShipperInputChange = useCallback((_, value, reason) => {
    setShipperInputValue(value || "");

    // Only fire on actual user typing — ignore "reset" (option selected/cleared).
    // Without this, every selection triggers a redundant API call.
    if (reason === "clear") {
      if (shipperDebounceRef.current) clearTimeout(shipperDebounceRef.current);
      setShipperOptions([]);
      setShipperLoading(false);
      return;
    }
    if (reason !== "input") return;

    const searchVal = value || "";
    const trimmedSearchVal = searchVal.trim();

    if (shipperDebounceRef.current) clearTimeout(shipperDebounceRef.current);

    if (trimmedSearchVal.length < SHIPPER_SEARCH_MIN_CHARS) {
      setShipperOptions([]);
      setShipperLoading(false);
      return;
    }

    // 500ms debounce — waits until the user pauses before calling the API
    shipperDebounceRef.current = setTimeout(async () => {
      const locationNumber = locationNumberRef.current;
      if (!locationNumber) return;
      setShipperLoading(true);
      try {
        const query = `?searchVal=${encodeURIComponent(trimmedSearchVal)}`;
        const res = await GET(`/shippers/${locationNumber}${query}`);
        const nextOptions = Array.isArray(res?.data) ? res.data.slice(0, MAX_SHIPPER_OPTIONS) : [];
        setShipperOptions(nextOptions);
      } catch (_error) {
        setShipperOptions([]);
      } finally {
        setShipperLoading(false);
      }
    }, 500);
  }, [locationNumberRef]);

  // Cleanup any pending debounce on unmount.
  useEffect(() => {
    return () => {
      if (shipperDebounceRef.current) clearTimeout(shipperDebounceRef.current);
    };
  }, []);

  // O(1) id sets — avoids O(n²) .includes() inside Autocomplete value derivation
  const docIdSet   = useMemo(() => new Set(documentTypes), [documentTypes]);
  const eventIdSet = useMemo(() => new Set(eventTypes),    [eventTypes]);

  const selectedDocValues = useMemo(
    () => documentTypeOptions.filter((o) => docIdSet.has(o.id)),
    [documentTypeOptions, docIdSet],
  );
  const selectedEventValues = useMemo(
    () => eventsTypeOptions.filter((o) => eventIdSet.has(o.id)),
    [eventsTypeOptions, eventIdSet],
  );

  // Ensure current shipper value is always in options so MUI can match it on edit,
  // and deduplicate by code since the API can return duplicate codes.
  const shipperOptionsResolved = useMemo(() => {
    const base = shipper && !shipperOptions.some((o) => o.code === shipper.code)
      ? [shipper, ...shipperOptions]
      : shipperOptions;
    const seen = new Set();
    return base.filter((o) => {
      if (seen.has(o.code)) return false;
      seen.add(o.code);
      return true;
    });
  }, [shipper, shipperOptions]);

  // Populate / clear form when editingRow changes
  useEffect(() => {
    if (editingRow) {
      setDocumentTypes(
        (editingRow.documentTypes || []).map((dt) =>
          typeof dt === "object" ? dt.id : dt,
        ),
      );
      setEventTypes(
        (editingRow.eventTypes || []).map((et) =>
          typeof et === "object" ? et.id : et,
        ),
      );
      setShipper(
        editingRow.shipperCode
          ? {
              code: editingRow.shipper?.code || editingRow.shipperCode,
              name: editingRow.shipper?.name || "",
            }
          : null,
      );
      setShipperInputValue(
        editingRow.shipperCode
          ? `${editingRow.shipper?.code || editingRow.shipperCode}  ${editingRow.shipper?.name || ""}`
          : "",
      );
      setEmail(normalizeEmails(editingRow.email));
      setFormError("");
    } else {
      setDocumentTypes([]);
      setEventTypes([]);
      setShipper(null);
      setShipperInputValue("");
      setEmail([]);
      setFormError("");
    }
  }, [editingRow]);

  // Reset form when parent signals a completed new-add (resetKey increments).
  // useEffect on editingRow alone can't handle this because editingRow stays null
  // both before and after a new add, so it never re-fires.
  useEffect(() => {
    if (resetKey === 0) return; // skip initial mount
    setDocumentTypes([]);
    setEventTypes([]);
    setShipper(null);
    setShipperInputValue("");
    setEmail([]);
    setFormError();
  }, [resetKey]);

  const handleSubmit = useCallback(() => {
    setFormError("");
    if (documentTypes.length === 0) {
      setFormError("Please select at least one document type.");
      return;
    }
    if (!isValidEmailList(email)) {
      setFormError("Please enter valid email(s).");
      return;
    }
    onSubmit({ documentTypes, eventTypes, shipper, email });
  }, [documentTypes, eventTypes, shipper, email, onSubmit]);

  const isEdit = !!editingRow;

  return (
    <Paper variant="outlined" sx={{ p: 1, flexShrink: 0 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
        <Autocomplete
          multiple
          size="small"
          fullWidth
          disableCloseOnSelect
          options={documentTypeOptions}
          value={selectedDocValues}
          onChange={(_, values) => setDocumentTypes(values.map((v) => v.id))}
          getOptionLabel={(o) => o?.name || ""}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          renderOption={(props, option) => {
            const idx = props["data-option-index"] ?? option.id;
            return (
              <li {...props} key={`doc_${idx}`}>
                {option.name}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField {...params} label="Document Type" placeholder="Select" />
          )}
        />

        <Autocomplete
          multiple
          size="small"
          fullWidth
          disableCloseOnSelect
          options={eventsTypeOptions}
          value={selectedEventValues}
          onChange={(_, values) => setEventTypes(values.map((v) => v.id))}
          getOptionLabel={(o) => o?.name || ""}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          renderOption={(props, option) => {
            const idx = props["data-option-index"] ?? option.id;
            return (
              <li {...props} key={`evt_${idx}`}>
                {option.name}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField {...params} label="Event Type" placeholder="Select" />
          )}
        />

        <Autocomplete
          size="small"
          fullWidth
          loading={shipperLoading}
          options={shipperOptionsResolved}
          value={shipper}
          inputValue={shipperInputValue}
          onInputChange={handleShipperInputChange}
          filterOptions={(options) => options}
          onChange={(_, newValue) =>
            setShipper(
              newValue && typeof newValue === "object"
                ? { code: newValue.code || "", name: newValue.name || "" }
                : null,
            )
          }
          isOptionEqualToValue={(o, v) => o?.code === v?.code}
          getOptionLabel={(o) => (o?.code ? `${o.code}  ${o.name || ""}` : "")}
          noOptionsText={
            shipperInputValue.trim().length < SHIPPER_SEARCH_MIN_CHARS
              ? `Type at least ${SHIPPER_SEARCH_MIN_CHARS} characters`
              : "No matches"
          }
          renderOption={(props, option) => {
            const idx = props["data-option-index"] ?? option.code;
            return (
              <li {...props} key={`shp_${idx}`}>
                {option.code}{"  "}{option.name || ""}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField {...params} label="Shipper / Consignee" placeholder="Search" />
          )}
        />
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mt: 1 }}>
        <Box sx={{ flex: 1 }}>
          <EmailInputField
            value={email}
            onChange={setEmail}
            multiple={true}
            label="Add email address(es)"
            placeholder="Type email and press Enter or comma"
            size="small"
          />
        </Box>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Button
            size="small"
            onClick={handleSubmit}
            endIcon={<AddCircleOutlineIcon />}
            disabled={saving}
          >
            {isEdit ? "Update" : "Add"}
          </Button>
          {isEdit && (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={onCancelEdit}
              disabled={saving}
            >
              Cancel
            </Button>
          )}
        </Box>
      </Stack>

      {formError && (
        <Typography sx={{ mt: 0.75, fontSize: "11px", color: "#c62828" }}>
          {formError}
        </Typography>
      )}
    </Paper>
  );
});

// ---------------------------------------------------------------------------
// Main component — owns rows / pagination / saving only.
// Shipper state is fully inside FormPanel — parent never re-renders from it.
// All props to FormPanel are stable references (backed by refs, [] deps).
// ---------------------------------------------------------------------------
export default function OrderByEventsOrDocumentation({ code }) {
  const dispatch       = useDispatch();
  const ordersLocation = useSelector(({ orderReducer }) => orderReducer.ordersLocation);
  const locationList   = useSelector(({ loadsReducer }) => loadsReducer.locationList);

  const allowedLocationNumbers = useMemo(() => {
    if (!Array.isArray(locationList)) return new Set();
    return new Set(locationList.map((l) => l.locationNumber));
  }, [locationList]);

  // Stable refs for columns renderCell (columns defined once with [] deps)
  const handleEditRef             = useRef(() => {});
  const handleDeleteRef           = useRef(() => {});
  const getEventTypeNameRef       = useRef((id) => id);
  const allowedLocationNumbersRef = useRef(new Set());

  // Stable refs for FormPanel props — synced every render, zero new references
  const editingRowRef          = useRef(null);
  const documentTypeOptionsRef = useRef([]);
  const eventsTypeOptionsRef   = useRef([]);
  const ordersLocationRef      = useRef(null);
  // locationNumberRef passed directly to FormPanel — it reads .current at call time
  const locationNumberRef      = useRef(null);

  const [rows,            setRows]            = useState([]);
  const [totalCount,      setTotalCount]      = useState(0);
  const [loading,         setLoading]         = useState(false);
  const [masterLoading,   setMasterLoading]   = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [saveError,       setSaveError]       = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const [documentTypeOptions, setDocumentTypeOptions] = useState([]);
  const [eventsTypeOptions,   setEventsTypeOptions]   = useState([]);
  const [editingRow,          setEditingRow]          = useState(null);
  const [resetKey,            setResetKey]            = useState(0);

  // Sync all mutable refs every render
  editingRowRef.current             = editingRow;
  documentTypeOptionsRef.current    = documentTypeOptions;
  eventsTypeOptionsRef.current      = eventsTypeOptions;
  ordersLocationRef.current         = ordersLocation;
  locationNumberRef.current         = ordersLocation?.locationNumber ?? null;
  allowedLocationNumbersRef.current = allowedLocationNumbers;

  // ---- Master data ----
  const mapDocTypes = useCallback((res) => {
    const source = Array.isArray(res?.data?.data) ? res.data.data
      : Array.isArray(res?.data) ? res.data : [];
    return source
      .map((item) => ({ id: item.id, name: item.abbrevation }))
      .filter((item) => item.id !== undefined && item.id !== null);
  }, []);

  const mapEventTypes = useCallback((res) => {
    const source = Array.isArray(res?.data?.data) ? res.data.data
      : Array.isArray(res?.data) ? res.data : [];
    return source
      .map((item) => ({
        id:   item.id ?? item.code ?? item.event_code,
        name: item.abbreviation ?? item.abbrevation ?? item.name ?? item.event_code ?? item.code,
      }))
      .filter((item) => item.id !== undefined && item.id !== null);
  }, []);

  const getEventTypeName = useCallback(
    (id) => eventsTypeOptions.find((o) => o.id === id)?.name || id,
    [eventsTypeOptions],
  );
  getEventTypeNameRef.current = getEventTypeName;

  useEffect(() => {
    let active = true;
    const fetchMasterData = async () => {
      setMasterLoading(true);
      try {
        const [docsRes, eventsRes] = await Promise.all([
          dispatch(getDocumentTypeData()),
          dispatch(getEventsTypeData()),
        ]);
        if (!active) return;
        setDocumentTypeOptions(mapDocTypes(docsRes));
        setEventsTypeOptions(mapEventTypes(eventsRes));
      } finally {
        if (active) setMasterLoading(false);
      }
    };
    fetchMasterData();
    return () => { active = false; };
  }, [dispatch, mapDocTypes, mapEventTypes]);

  // ---- Rows ----
  const fetchRows = useCallback(async () => {
    let mounted = true;
    try {
      setLoading(true);
      const res = await dispatch(
        getOrderByDocumentation(
          ordersLocation?.locationNumber,
          code,
          paginationModel.page,
          paginationModel.pageSize,
        ),
      );
      if (!mounted) return;
      const records = res?.data?.orderDocuments || [];
      console.log("Fetched Order By Documentation Records:", records);
      setRows(
        records.map((r) => ({
          ...r,
          email: normalizeEmails(r.email),
          _isNew: false,
          _deleting: false,
          _localCreatedAt: null,
        })),
      );
      setTotalCount(res?.data?.totalCount || 0);
    } finally {
      if (mounted) setLoading(false);
      mounted = false;
    }
  }, [dispatch, ordersLocation, code, paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    if (!code) return;
    fetchRows();
  }, [code, fetchRows]);

  // ---- Form submit — STABLE ([] deps), reads live values via refs ----
  const handleFormSubmit = useCallback(
    async ({ documentTypes, eventTypes, shipper, email }) => {
      setSaveError("");
      const currentEditingRow   = editingRowRef.current;
      const currentDocOptions   = documentTypeOptionsRef.current;
      const currentEventOptions = eventsTypeOptionsRef.current;
      const currentLocation     = ordersLocationRef.current;

      const isEdit = !!currentEditingRow;

      const docObjects = documentTypes.map((id) => {
        const found = currentDocOptions.find((o) => o.id === id);
        return found ? { id: found.id, name: found.name } : { id, name: String(id) };
      });
      const eventObjects = eventTypes.map((id) => {
        const found = currentEventOptions.find((o) => o.id === id);
        return found ? { id: found.id, name: found.name } : { id, name: String(id) };
      });

      const payload = {
        locationNumber:  currentLocation?.locationNumber,
        documentTypes:   docObjects,
        eventTypes:      eventObjects,
        shipper:         shipper || null,
        shipperCode:     shipper?.code || null,
        email:           normalizeEmails(email),
        documentTypeIds: documentTypes,
        eventTypeIds:    eventTypes,
      };
      if (isEdit) payload.ruleId = currentEditingRow.ruleId;

      setSaving(true);
      const res = await dispatch(
        saveOrderByDocumentation(currentLocation?.locationNumber, code, [payload]),
      );
      setSaving(false);

      if (res?.status === "success" || res?.httpCode === 200) {
        setEditingRow(null);
        if (!isEdit) setResetKey((k) => k + 1);
        await fetchRows();
        return;
      }

      setSaveError("Save failed. Please try again.");
    },
    [dispatch, code, fetchRows],
  );

  // STABLE — [] deps
  const handleCancelEdit = useCallback(() => setEditingRow(null), []);

  // ---- Edit / Delete ----
  const handleEdit = useCallback((row) => setEditingRow(row), []);
  handleEditRef.current = handleEdit;

  const handleDelete = useCallback(
    async (row) => {
      if (String(row.ruleId).startsWith("new_")) {
        setRows((prev) => prev.filter((r) => r.ruleId !== row.ruleId));
        return;
      }
      setRows((prev) =>
        prev.map((r) => r.ruleId === row.ruleId ? { ...r, _deleting: true } : r),
      );
      const res = await dispatch(deleteOrderByDocumentation(code, row.ruleId));
      if (res?.status === "success") {
        setRows((prev) => prev.filter((r) => r.ruleId !== row.ruleId));
      } else {
        setRows((prev) =>
          prev.map((r) => r.ruleId === row.ruleId ? { ...r, _deleting: false } : r),
        );
      }
    },
    [dispatch, code],
  );
  handleDeleteRef.current = handleDelete;

  // ---- Display rows ----
  const displayRows = useMemo(() => rows, [rows]);

  // ---- Columns — defined ONCE ([] deps), all dynamic calls via stable refs ----
  const columns = useMemo(
    () => [
      {
        field: "locationNumber",
        headerName: "Loc#",
        width: 80,
      },
      {
        field: "documentTypes",
        headerName: "Document Type",
        flex: 1.2,
        minWidth: 220,
        sortable: false,
        renderCell: ({ row }) => {
          const text = (row.documentTypes || []).map((dt) => dt.name).join(", ");
          return (
            <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: "100%" }}>
              {text || "-"}
            </Box>
          );
        },
      },
      {
        field: "eventTypes",
        headerName: "Event Type",
        flex: 1,
        minWidth: 180,
        sortable: false,
        renderCell: ({ row }) => {
          const text = (row.eventTypes || [])
            .map((et) => (typeof et === "object" ? et.name : getEventTypeNameRef.current(et)))
            .join(", ");
          return (
            <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: "100%" }}>
              {text || "-"}
            </Box>
          );
        },
      },
      {
        field: "shipper",
        headerName: "Shipper / Consignee",
        flex: 1,
        minWidth: 180,
        sortable: false,
        valueGetter: (params) =>
          params?.row?.shipperCode ? `${params.row.shipperCode}` : "-",
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1,
        minWidth: 220,
        sortable: false,
        valueGetter: (params) =>
          normalizeEmails(params?.row?.email).join(", ") || "-",
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 100,
        sortable: false,
        renderCell: ({ row }) => {
          const isAllowed = allowedLocationNumbersRef.current.has(row.locationNumber);
          if (!isAllowed) return null;
          return (
            <>
              <IconButton
                size="small"
                onClick={() => handleEditRef.current(row)}
                disabled={row._deleting}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleDeleteRef.current(row)}
                disabled={row._deleting}
              >
                {row._deleting ? (
                  <CircularProgress size={16} />
                ) : (
                  <DeleteIcon fontSize="small" />
                )}
              </IconButton>
            </>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // ---- Render ----
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <FormPanel
        documentTypeOptions={documentTypeOptions}
        eventsTypeOptions={eventsTypeOptions}
        locationNumberRef={locationNumberRef}
        editingRow={editingRow}
        resetKey={resetKey}
        saving={saving}
        onSubmit={handleFormSubmit}
        onCancelEdit={handleCancelEdit}
      />

      {saveError && (
        <Typography sx={{ fontSize: "11px", color: "#c62828", px: 0.5 }}>
          {saveError}
        </Typography>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflowX: "auto", overflowY: "hidden" }}>
        <DataGridPro
          rows={displayRows}
          columns={columns}
          getRowId={(row) => row.ruleId}
          loading={loading || masterLoading}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          pageSizeOptions={[10, 25, 50]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          disableRowSelectionOnClick
          density="compact"
          sx={{
            height: "100%",
            minWidth: 700,
            border: "1px solid #e5eaf0",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#0b4387",
              color: "#fff",
              minHeight: "38px !important",
              maxHeight: "38px !important",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: "12px",
              fontWeight: 700,
            },
            "& .MuiDataGrid-cell": { py: 0.5 },
            "& .MuiDataGrid-footerContainer": { minHeight: 40 },
          }}
        />
      </Box>
    </Box>
  );
}
